'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { AuthenticationService } from '@/services/AuthenticationService';
import { ErrorHandlerService } from '@/services/ErrorHandlerService';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { LockClosedIcon, KeyIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import type { PageUrlMap } from '@/types/blog';
import { resolveUrlFromMap } from '@/lib/blog-transformers';

interface SetPasswordPageProps {
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
}

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#_\-])[A-Za-z\d@$!%*?&#_\-]{8,}$/;

export default function SetPasswordPage({
  language = 'en',
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
}: SetPasswordPageProps) {
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [paramsReady, setParamsReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    setToken(params.get('token') || '');
    setEmail(params.get('email') || '');
    setParamsReady(true);
  }, []);

  const { getVariable, language: currentLocale } = useLanguageVariables();
  const t = (key: string, fallback: string) => translations[key] || getVariable(key, fallback) || fallback;

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const loginUrl = resolveUrlFromMap(pageUrls, 'login', language, defaultLanguage, '/login');

  useEffect(() => {
    if (paramsReady && (!token || !email)) {
      setError(t('auth.set_password.invalid_link', 'Geçersiz veya eksik link. Lütfen e-postanızdaki şifre oluşturma linkini kullanın.'));
    }
  }, [paramsReady, token, email, t]);

  const validatePassword = (): string | null => {
    if (password.length < 8) {
      return t('auth.set_password.min_length', 'Şifre en az 8 karakter olmalıdır.');
    }
    if (!PASSWORD_REGEX.test(password)) {
      return t('auth.set_password.complexity', 'Şifre en az bir büyük harf, bir küçük harf, bir rakam ve bir özel karakter (#, @, !, vb.) içermelidir.');
    }
    if (password !== passwordConfirmation) {
      return t('auth.set_password.mismatch', 'Şifreler eşleşmiyor.');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const response = await AuthenticationService.resetPassword({
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
        language: currentLocale || language,
      });

      if (response.success) {
        setSuccess(true);
        setError(null);
      } else {
        const displayError = response.error_key
          ? t(response.error_key, t('auth.set_password.failed', 'Failed to set password.'))
          : ErrorHandlerService.cleanErrorMessage(
              response.error || response.message || '',
              t('auth.set_password.failed', 'Failed to set password.')
            );
        setError(displayError);
      }
    } catch (err) {
      const rawError = err instanceof Error ? err.message : '';
      setError(
        rawError ? ErrorHandlerService.cleanErrorMessage(rawError, t('auth.set_password.failed', 'Failed to set password.')) : t('auth.set_password.failed', 'Failed to set password.')
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-neutral-200/90 p-8 text-center">
          <div className="inline-flex p-4 bg-green-100 rounded-full mb-4">
            <CheckCircleIcon className="w-16 h-16 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {t('auth.set_password.success_title', 'Şifreniz Başarıyla Oluşturuldu')}
          </h2>
          <p className="text-gray-600 mb-6 text-base">
            {t('auth.set_password.success_message', 'Şifreniz başarıyla oluşturuldu. Artık giriş yapabilirsiniz.')}
          </p>
          <Link
            href={loginUrl}
            className="inline-flex items-center justify-center w-full px-6 py-3.5 bg-alsero-first text-white font-semibold rounded-lg hover:opacity-90 transition-colors shadow-md"
          >
            {t('auth.set_password.login_button', 'Giriş Yap')}
          </Link>
        </div>
      </div>
    );
  }

  if (!paramsReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-neutral-200/90 p-8 text-center">
          <div className="animate-pulse text-gray-400">{t('auth.set_password.loading', 'Yükleniyor...')}</div>
        </div>
      </div>
    );
  }

  if (!token || !email) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-neutral-200/90 p-8 text-center">
          <KeyIcon className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {t('auth.set_password.invalid_link_title', 'Geçersiz Link')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            href={loginUrl}
            className="inline-flex items-center justify-center px-6 py-3 bg-alsero-first text-white font-medium rounded-lg hover:opacity-90 transition-colors"
          >
            {t('auth.login.title', 'Giriş Yap')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12 overflow-y-auto">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-neutral-200/90 p-8 pb-10 overflow-visible">
        <div className="text-center mb-6">
          <div className="inline-flex p-3 bg-alsero-first/10 rounded-full mb-4">
            <LockClosedIcon className="w-10 h-10 text-alsero-first" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('auth.set_password.title', 'Şifrenizi Oluşturun')}
          </h1>
          <p className="text-gray-600 mt-2">
            {t('auth.set_password.subtitle', 'Hesabınız için bir şifre belirleyin.')}
          </p>
          <p className="text-sm text-gray-500 mt-1">{email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 flex flex-col">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.set_password.password_label', 'Yeni Şifre')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/50 focus:border-alsero-first"
              placeholder="••••••••"
              required
              minLength={8}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('auth.set_password.password_hint', 'En az 8 karakter, büyük harf, küçük harf, rakam ve özel karakter içermelidir.')}
            </p>
          </div>

          <div>
            <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.set_password.confirm_label', 'Şifre Tekrar')}
            </label>
            <input
              id="password_confirmation"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/50 focus:border-alsero-first"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 px-4 mt-4 shrink-0 bg-alsero-first hover:opacity-90 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-alsero-first/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
          >
            {loading ? t('auth.set_password.submitting', 'İşleniyor...') : t('auth.set_password.submit', 'Şifreyi Oluştur')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600">
          {t('auth.set_password.back_to_login', 'Zaten hesabınız var mı?')}{' '}
          <Link href={loginUrl} className="font-medium text-alsero-first hover:opacity-80">
            {t('auth.login.title', 'Giriş Yap')}
          </Link>
        </p>
      </div>
    </div>
  );
}
